import { Grid } from '@chakra-ui/react';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ModalBody,
  ModalContent,
  ModalFooterDefault,
  ModalHeader,
  useSnackbar,
} from 'ui-components';
import { v4 as uuidv4 } from 'uuid';
import { smartProfileApi } from '~/api/smartProfile';
import { waitForCondition } from '~/asyncUtils';
import { CreatorPlatformsType } from '~/clientApi';
import { CreatorGender } from '~/clientApi/creator.model';
import { ImageUploaderState } from '~/components/ImageUploader/ImageUploader';
import { FEATURE_FLAGS } from '~/constants';
import { formatDateStringForDatePicker } from '~/core/utils/date';
import { hasOnlyEmptyValues } from '~/core/utils/object';
import { AddPlatformResponse } from '~/hooks/api/useAddPlatform';
import {
  SmartProfileFieldStatus,
  SmartProfileInformationResponse,
} from '~/hooks/api/useGetSmartProfileInformation';
import { useUpdateTalent } from '~/hooks/api/useUpdateTalent';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { Bio } from '~/screens/AddTalent/ProfileInformation/Bio';
import { LocationGenderDob } from '~/screens/AddTalent/ProfileInformation/LocationGenderDob';
import { NameAndAvatar } from '~/screens/AddTalent/ProfileInformation/NameAndAvatar';
import { Verticals } from '~/screens/AddTalent/ProfileInformation/Verticals';
import { parseHandle } from '~/screens/ManagePlatforms';
import { useCreatorStore } from '~/store/creator.store';
import { ICreatorLocation } from '~/types';

import { Platform } from '../constants';
import { useAddTalentStore } from '../useAddTalentStore';
import {
  useGetSmartProfile,
  useGetSmartProfileStreaming,
} from '../useGetSmartProfile';
import { getPlatformKey } from '../utils';
import { transformSmartProfileRespToFieldValueObj } from './utils';

export type TalentValues = {
  avatar?: null | string | Blob;
  bio?: string;
  coordinates?: ImageUploaderState['coordinates'];
  croppedImage?: null | Blob;
  dateOfBirth?: string | null;
  firstName: string;
  gender?: CreatorGender;
  id?: string;
  lastName?: string;
  location?: ICreatorLocation;
  verticals?: string[];
  smartData?: {
    [K in keyof SmartProfileInformationResponse]?: {
      value: string;
      status: SmartProfileFieldStatus;
    };
  };
  updatedAt?: string;
};

type CreatorProfileInfo = {
  id?: string;
  avatar?: string;
  firstName: string;
  lastName?: string;
  location?: TalentValues['location'];
  gender?: CreatorGender;
  dateOfBirth?: string;
  bio?: string;
  verticals?: string[];
  platforms?: CreatorPlatformsType;
  updatedAt?: string;
};

interface Props {
  creator: CreatorProfileInfo;
  isEdit?: boolean;
  title?: string;
  setDirty?: (isDirty: boolean) => void;
  onClose: () => void;
  onUpdate: (response?: AddPlatformResponse) => void;
}

export const ProfileInformation: React.FC<Props> = ({
  onClose,
  onUpdate,
  isEdit = false,
  title = 'Add Talent profile information',
  creator,
  setDirty,
}) => {
  const creatorStore = useCreatorStore();
  const snackbar = useSnackbar();

  const { isEnabled: isStreamingEnabled } = useFeature(
    FEATURE_FLAGS.foamStreamingPoc,
  );

  const updateTalent = useUpdateTalent();

  const {
    smartProfileRequestId,
    smartProfileEnabled: toggleSmartProfileEnabled,
    platforms,
    formValues,
    isFormTouched,
    resetProfileFormState,
  } = useAddTalentStore();

  const useSmartProfileHook = isStreamingEnabled
    ? useGetSmartProfileStreaming
    : useGetSmartProfile;

  const {
    data: creatorSmartProfileData,
    error: cSPError,
    fetchCreatorProfile,
    isFetching: isCSPFetching,
    isSuccess,
  } = useSmartProfileHook(
    smartProfileRequestId,
    toggleSmartProfileEnabled,
    creator?.platforms,
  );

  const { isEnabled: isSmartCreatorProfileEnabled } = useFeature(
    FEATURE_FLAGS.foamSmartCreatorProfiles,
  );

  const hasNonSnapchatPlatforms = useMemo(() => {
    if (!creator.platforms || Object.keys(creator.platforms).length === 0)
      return false;
    return Object.entries(creator.platforms).some(
      ([platform, accountInfo]) =>
        platform !== 'snapchat' && accountInfo.handle,
    );
  }, [creator.platforms]);

  const handleAddUpdateTalent = (values: TalentValues, isEdit = false) => {
    const data = new FormData();

    const {
      avatar,
      bio,
      coordinates,
      croppedImage,
      dateOfBirth,
      firstName,
      gender,
      id,
      lastName,
      location,
      verticals,
    } = values;

    data.append('firstName', firstName);

    if (avatar) {
      data.append('avatar', avatar);
      data.append('width', '1000');
      data.append('height', '1000');

      if (coordinates) {
        data.append(
          'cropCoordinates',
          JSON.stringify({
            height: coordinates.height,
            width: coordinates.width,
            x: coordinates.x,
            y: coordinates.y,
          }),
        );
      }
    }

    // using == instead === because we are checking for null and undefined
    if (bio != null) data.append('biography', bio);
    if (dateOfBirth != null) data.append('dateOfBirth', dateOfBirth);
    if (gender != null) data.append('gender', gender);
    if (lastName != null) data.append('lastName', lastName);
    if (location != null) data.append('location', JSON.stringify(location));
    if (verticals != null) data.append('category', verticals.toString());

    platforms.forEach((platform) => {
      const platformKey = getPlatformKey(platform.platform as Platform);
      const platformHandle = parseHandle(platform.handle) || '';
      data.append(platformKey, platformHandle);
    });

    const updateTalentOrPlatform = () => {
      updateTalent.mutate(
        {
          id: !isEdit ? uuidv4() : (id as string),
          updatedAt: creator.updatedAt as string,
          creator: data,
          type: 'formdata',
        },
        {
          onError: (error) => {
            snackbar.error({
              description:
                error.response?.status === 413
                  ? 'Upload failed. Please try again or contact Support.'
                  : 'Something went wrong. Please try again!',
            });
          },
          onSuccess: async (resp: AddPlatformResponse) => {
            if (smartProfileRequestId) {
              smartProfileApi.patch(
                `smart-profiles/profile-information/${smartProfileRequestId}`,
                {
                  creatorId: resp.data.id,
                },
              );
            }

            onUpdate(resp);

            // set temp blob in store
            if (id && croppedImage) {
              creatorStore.setCroppedImage(id, croppedImage);
            }
          },
        },
      );
    };

    updateTalentOrPlatform();
  };

  const dateOfBirth = useMemo(() => {
    if (creator.dateOfBirth) {
      return formatDateStringForDatePicker(creator.dateOfBirth);
    }

    return;
  }, [creator.dateOfBirth]);

  const initialValues = useMemo(() => {
    // If we have touched form values in the store, use those to initialise
    if (isFormTouched && formValues) {
      return { ...formValues };
    }

    return {
      ...creator,
      // This check is necessary until the data is normalized in BE, until then empty values could be null or { id: "", city: "", ...}
      location: hasOnlyEmptyValues(creator.location) ? null : creator.location,
      dateOfBirth,
    };
  }, [creator, dateOfBirth, formValues, isFormTouched]);

  const isAnyFieldEmpty = (values: Partial<TalentValues>) => {
    return (
      !values.firstName ||
      !values.lastName ||
      !values.bio ||
      !values.location ||
      !values.gender ||
      !values.dateOfBirth ||
      !values.verticals ||
      values.verticals.length === 0
    );
  };

  const handleFetchProfile = useCallback(async () => {
    // smart button's loading state is handled correctly if we properly handle the Promise chain and throw the error if any
    // eslint-disable-next-line no-useless-catch
    try {
      await fetchCreatorProfile();

      if (cSPError) {
        throw cSPError;
      }

      await waitForCondition(
        () => isSuccess,
        300000, // 5 minutes timeout
        1000, // Check every second
      );
    } catch (error) {
      throw error;
    }
  }, [fetchCreatorProfile, isSuccess, cSPError]);

  const formikRef = useRef<FormikProps<TalentValues>>(null);
  const { setFormValues, setIsFormTouched } = useAddTalentStore();

  const handleBack = useCallback(() => {
    if (isEdit) {
      resetProfileFormState();
    } else {
      // Sync current form values to store before going back
      // Sync only touched files so we ignore updates from smartProfile data
      if (formikRef.current) {
        const { values, touched } = formikRef.current;
        const touchedValues = Object.entries(touched)
          .filter(
            ([key, isTouched]) =>
              isTouched && values[key as keyof TalentValues],
          )
          .reduce(
            (acc, [key]) => ({
              ...acc,
              [key]: values[key as keyof TalentValues],
            }),
            {} as Partial<TalentValues>,
          );

        if (Object.keys(touchedValues).length > 0) {
          setFormValues(touchedValues);
          setIsFormTouched(true);
        }
      }
    }
    onClose();
  }, [isEdit, onClose, resetProfileFormState, setFormValues, setIsFormTouched]);

  useEffect(() => {
    return () => {
      if (isEdit) {
        resetProfileFormState();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) =>
        handleAddUpdateTalent(values as TalentValues, isEdit)
      }
      validateOnChange
      validate={(values) => {
        const errors = {};

        if (!values.firstName) {
          // @ts-ignore
          errors.firstName = 'First name is required';
        }
        return errors;
      }}
    >
      {({ values }) => {
        const checkIfCTAisDisabled = () => {
          if (isEdit) {
            return isCSPFetching;
          }
          return toggleSmartProfileEnabled ? !isSuccess : false;
        };

        return (
          <>
            <ModalContent as={Form}>
              <ModalHeader>{title}</ModalHeader>
              <ModalBody>
                <Grid gap={6}>
                  <NameAndAvatar />
                  <LocationGenderDob />
                  <Bio />
                  <Verticals />
                </Grid>
              </ModalBody>
              <ModalFooterDefault
                primary={{
                  ariaLabel: !isEdit ? 'add' : 'save',
                  title: !isEdit ? 'Add' : 'Save',
                  type: 'submit',
                  isDisabled: checkIfCTAisDisabled(),
                  isLoading: updateTalent.isLoading,
                  size: 'md',
                  trackId: !isEdit
                    ? 'foam-add-talent-profile-info-add'
                    : 'foam-edit-talent-profile-info-save',
                }}
                secondary={{
                  type: 'reset',
                  ariaLabel: !isEdit ? 'back' : 'cancel',
                  onClick: handleBack,
                  title: !isEdit ? 'Back' : 'Cancel',
                  isDisabled: updateTalent.isLoading,
                  size: 'md',
                  trackId: !isEdit
                    ? 'foam-add-talent-profile-info-back'
                    : 'foam-edit-talent-profile-info-cancel',
                }}
                tertiary={{
                  component: 'SmartButton',
                  ariaLabel: 'tertiary',
                  title: 'Fill with Smart Profile',
                  onClick: handleFetchProfile,
                  status: isCSPFetching ? 'loading' : 'idle',
                  hidden: isStreamingEnabled
                    ? false
                    : !isSmartCreatorProfileEnabled ||
                      !creator.id ||
                      !isAnyFieldEmpty(values) ||
                      isSuccess ||
                      !hasNonSnapchatPlatforms,
                  type: 'button',
                  trackId: !isEdit
                    ? 'foam-add-talent-profile-info-smart-profile'
                    : 'foam-edit-talent-profile-info-smart-profile',
                }}
              />
            </ModalContent>
            <FormUpdater
              setIsDirty={setDirty}
              creatorSmartProfileData={creatorSmartProfileData}
            />
          </>
        );
      }}
    </Formik>
  );
};

type FormUpdaterProps = {
  setIsDirty?: (isDirty: boolean) => void;
  creatorSmartProfileData?: SmartProfileInformationResponse;
};

const FormUpdater: React.FC<FormUpdaterProps> = ({
  setIsDirty,
  creatorSmartProfileData,
}) => {
  const { dirty, setValues } = useFormikContext<TalentValues>();
  const { isEnabled: isFoamTalentLocationFFEnabled } = useFeature(
    FEATURE_FLAGS.foamTalentLocation,
  );

  // Handle smart profile data updates
  useEffect(() => {
    if (creatorSmartProfileData) {
      const transformedData = transformSmartProfileRespToFieldValueObj(
        creatorSmartProfileData,
      );
      setValues((prevValues) => {
        const newValues = { ...prevValues };
        const smartData: Record<
          string,
          { value: string; status: SmartProfileFieldStatus }
        > = {};

        Object.entries(creatorSmartProfileData).forEach(([key, field]) => {
          smartData[key] = {
            value: field.value as string,
            status: field.status as SmartProfileFieldStatus,
          };
        });

        Object.entries(transformedData).forEach(([key, value]) => {
          if (key === 'verticals') {
            // because verticals is an array we need to make sure it is empty before replacing it
            if (!prevValues.verticals || prevValues.verticals.length === 0) {
              newValues.verticals = value as string[];
            }
          } else if (key === 'location') {
            if (isFoamTalentLocationFFEnabled && !prevValues.location) {
              newValues.location = value as ICreatorLocation;
            }
          } else if (
            !prevValues[key as keyof TalentValues] &&
            value !== undefined
          ) {
            (newValues as any)[key] = value;
          }
        });

        return {
          ...newValues,
          smartData,
        };
      });
    }
  }, [creatorSmartProfileData, setValues, isFoamTalentLocationFFEnabled]);

  useEffect(() => {
    if (setIsDirty) {
      setIsDirty(dirty);
    }
  }, [dirty, setIsDirty]);

  return null;
};
